import { Injectable } from '@angular/core';
import { LANGUAGES } from '@constants/app.constants';
import { environment } from '@environments/environment';
import { LoggerService } from '@services/utils/logger.service';

@Injectable({
  providedIn: 'root'
})
export class HelperService {

  // Navigate to an external URL in a new tab. `noopener,noreferrer` severs window.opener and strips
  // the Referer header (reverse-tabnabbing protection). NOTE: the browser may still block this when
  // it is not triggered by a direct user gesture (the summary-view / Stripe-return auto-redirect).
  redirectToUrl(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  sort(collection: Array<any>): Array<any> {
    collection.sort((a: any, b: any) => {
      if (a < b) {
        return -1;
      } else if (a > b) {
        return 1;
      } else {
        return 0;
      }
    });
    return collection;
  }

  sortByNumber(collection: Array<any>, property: string): Array<any> {
    collection.sort((a: any, b: any) => {
      if (a[property] < b[property]) {
        return -1;
      } else if (a[property] > b[property]) {
        return 1;
      } else {
        return 0;
      }
    });
    return collection;
  }

  sortBy(collection: Array<any>, property: string): Array<any> {
    collection.sort((a: any, b: any) => {
      if (a[property] < b[property]) {
        return -1;
      } else if (a[property] > b[property]) {
        return 1;
      } else {
        return 0;
      }
    });
    return collection;
  }

  shuffle(array: any[]): any[] {
    let currentIndex = array.length;
    let randomIndex: number;

    // While there remain elements to shuffle.
    while (currentIndex != 0) {
      // Pick a remaining element.
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }
    return array;
  }

  isEmptyString(value: string): string {
    if (value) {
      if (value.trim() === '') {
        return null;
      } else {
        return value;
      }
    } else {
      return null;
    }
  }

  replaceAll(str: string, find: string, replace: string): string {
    return str.replace(new RegExp(find, 'g'), replace);
  }

  mergeTimeRanges(collection: Array<any>, start: string, end: string): any[] {
    const tempList: any[] = [];
    let current: any;
    let previous: any;
    let index: number = Number(0);
    for (const item of collection) {
      if (!previous) {
        current = Object.assign({}, item);
        previous = Object.assign({}, item);
        if (collection.length === 1) {
          tempList.push(Object.assign({}, current));
        }
      } else {
        if (previous.end === item.start) {
          current.end = item.end;
          previous = Object.assign({}, item);
          if (typeof collection[index + 1] === 'undefined') {
            tempList.push(Object.assign({}, current));
          }
        } else {
          tempList.push(Object.assign({}, current));
          current = Object.assign({}, item);
          previous = Object.assign({}, item);
          if (typeof collection[index + 1] === 'undefined') {
            tempList.push(Object.assign({}, item));
          }
        }
      }
      index++;
    }
    return tempList;
  }

  getTeaserMessage(placeHolder: string): string {
    switch (placeHolder) {
      case '[TODAY]':
        return 'teaser_component.today_message';
      case '[TOMORROW]':
        return 'teaser_component.tomorrow_message';
      default:
        return 'teaser_component.date_message';
    }
  }

  groupBy(
    collection: Array<any>,
    groupByKey: string,
    sortByKey: string,
    sortNestedArrayKey?: string,
    additionalKey1?: string,
    additionalKey2?: string
  ): Array<any> {
    // prevents the application from breaking if the array of objects doesn't exist yet
    if (!collection) {
      return null;
    }

    if (groupByKey) {
      collection = this.sortBy(collection, sortByKey);
    } else {
      return null;
    }

    const groupedCollection = collection.reduce((previous, current) => {
      if (!previous[current[groupByKey]]) {
        previous[current[groupByKey]] = [current];
      } else {
        previous[current[groupByKey]].push(current);
      }

      return previous;
    }, {});
    LoggerService.log('groupedCollection ', groupedCollection);
    // LoggerService.log('Object.keys(groupedCollection) ', Object.keys(groupedCollection).sort());
    // this will return an array of objects, each object containing a group of objects
    const finalDataList: { key: any, value: any[], temp: any }[] = Object.keys(groupedCollection).sort().map(key => (
      {
        key,
        value: sortNestedArrayKey ? this.sortBy(groupedCollection[key], sortNestedArrayKey) : groupedCollection[key],
        temp: {}
      }));

    if (finalDataList && finalDataList.length > 0) {
      if (additionalKey1 || additionalKey2) {
        for (const finalData of finalDataList) {
          if (finalData.value && finalData.value.length > 0) {
            if (additionalKey1) {
              finalData.temp[additionalKey1] = finalData.value[0][additionalKey1];
            }
            if (additionalKey2) {
              finalData.temp[additionalKey2] = finalData.value[0][additionalKey2];
            }
          }
        }
      }
    }

    LoggerService.log('Inside CalioGroupByPipe finalDataList ', finalDataList);

    return finalDataList;
  }

  loadStripeJs(): void {
    try {
      // Stripe.js misbehaves if injected twice (payment-step revisit, or a host page that
      // already loads Stripe) — skip when any js.stripe.com/v3 tag exists.
      if (document.querySelector('script[src^="https://js.stripe.com/v3"]')) {
        return;
      }
      const script = document.createElement('script');
      script.src = `https://js.stripe.com/v3/`;
      document.head.appendChild(script);
    } catch (ex) {
      LoggerService.error(ex);
    }
  }

  findStringBetween(str: string, char1: string, char2: string): string {
    return str.split(char1).pop().split(char2)[0];
  }

  createArrayRange(range: number): number[] {
    return new Array(range);
  }

  /**
   * Identify specific storage is available or not
   * @param type Type of storage to check if it is available or not
   * @returns boolean
   */
  storageAvailable(type: string): boolean {
    try {
      const storage = window?.[type];
      const x = '__calenso_storage_test__';

      storage.setItem(x, x);
      storage.removeItem(x);

      return true;
    } catch (e) {
      return false;
    }
  }

  // NOTE: a URL-based `addSvgIcon` helper used to live here. It is gone deliberately - every
  // Mat icon is now registered from bundled markup via `MAT_ICON_LITERALS` + `addSvgIconLiteral`
  // in app.component.ts, because MatIconRegistry fetches through HttpClient and those requests
  // pick up the auth bearer, get preflighted, and are refused by the static origins. Re-adding a
  // URL-based registration reintroduces that silent failure in web-component mode.

  /**
  * Removes keys with null, undefined, empty arrays or empty objects from an object
  * @param obj The object to clean
  * @returns Cleaned object without empty values
  */
  removeEmptyValues(obj: any): any {
    if (obj === null || obj === undefined) {
      return undefined;
    }

    if (Array.isArray(obj)) {
      const filteredArr = obj.filter(item => {
        const cleaned = this.removeEmptyValues(item);
        return cleaned !== undefined;
      });
      return filteredArr.length ? filteredArr : undefined;
    }

    if (typeof obj === 'object') {
      const cleaned = Object.entries(obj).reduce((acc, [key, value]) => {
        const cleanedValue = this.removeEmptyValues(value);
        if (cleanedValue !== undefined) {
          acc[key] = cleanedValue;
        }
        return acc;
      }, {} as any);

      return Object.keys(cleaned).length ? cleaned : undefined;
    }

    return obj;
  }

  /**
  * Shuffles an array using Fisher-Yates algorithm
  * @param array Array to shuffle
  * @returns Shuffled array
  */
  shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  scrollToFirstInvalidControl(containerEl: HTMLElement): void {
    setTimeout(() => {
      const invalidElements = containerEl.querySelectorAll('.ng-invalid:not(form):not([hidden])');
      let topmost: Element = null;
      let topPos = Infinity;
      invalidElements.forEach((el: Element) => {
        const rect = el.getBoundingClientRect();
        if (rect.height > 0 && rect.top < topPos) {
          topPos = rect.top;
          topmost = el;
        }
      });
      if (topmost) {
        topmost.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  /**
   * Normalises any language string (full locale "de_CH", BCP-47 tag "de-CH", or short code "de")
   * to a supported Calenso locale, or undefined if unsupported. Region subtags collapse to the
   * language family ("de-DE" -> "de_CH") — so any German browser maps to the German service.
   */
  resolveLocale(language: string | undefined): string | undefined {
    if (!language) {
      return undefined;
    }
    const canonical = (value: string): string => value.toLowerCase().replace(/[-_]/g, '-');
    const normalized = canonical(language);
    const exactMatch = LANGUAGES.find(supported => canonical(supported.locale) === normalized);
    if (exactMatch) {
      return exactMatch.locale;
    }
    const primarySubtag = normalized.split('-')[0]; // "de-ch" -> "de"
    return LANGUAGES.find(supported => supported.value === primarySubtag)?.locale;
  }

  /**
   * Language family ("de", "en") for a full Calenso locale ("de_CH", "en_US"), or undefined.
   * Used to look up the language→service map by its region-agnostic key — a German visitor
   * (de_CH, de_DE, de_AT) and any English visitor (en_US, en_GB, en_AU, en_CA) each resolve to
   * a single family key, so the map reads "de"/"en" rather than a specific country locale.
   */
  languageOf(locale: string | undefined): string | undefined {
    return locale ? LANGUAGES.find(supported => supported.locale === locale)?.value : undefined;
  }

  /**
   * First supported Calenso locale from navigator.languages (e.g. "de-CH" -> "de_CH"),
   * or undefined when none match.
   *
   * `enabledLanguages` optionally constrains the result to the partner's enabled widget languages
   * — the language FAMILIES the backend returns in `supported_widget_languages` (["de", "en"], not
   * locales; see PartnersSettingsTable::getEnabledLanguages()). Pass it so a visitor whose browser
   * language Calenso supports but the partner has NOT enabled (pl-PL against a de/fr/it partner)
   * falls through to the next candidate rather than rendering an unenabled language.
   *
   * Omit the argument for an unconstrained lookup — the JB-1038 language→service mapping relies on
   * that, since a service may be mapped to a language the widget UI itself does not offer. An
   * EMPTY array is not the same as omitting it: it means "the partner enables nothing", so nothing
   * matches. That is the fail-safe reading — a config that never arrived degrades to the partner
   * default instead of guessing a language.
   */
  getBrowserLocale(enabledLanguages?: string[]): string | undefined {
    const browserLanguages = (navigator.languages?.length ? navigator.languages : [navigator.language]).filter(Boolean);
    for (const browserLanguage of browserLanguages) {
      const locale = this.resolveLocale(browserLanguage);
      if (!locale) {
        continue;
      }
      if (!enabledLanguages) {
        return locale;
      }
      // Compare families, not locales: the partner list is ["de", "en"], the resolved value "de_CH".
      const family = this.languageOf(locale);
      if (family && enabledLanguages.includes(family)) {
        return locale;
      }
    }
    return undefined;
  }

  /**
   * Real locale for a widget configured with the BROWSER_LOCALE_SENTINEL (JB-1634).
   *
   * Order: the first browser language that is both supported and enabled for the partner, then the
   * partner's default language when the partner still has it enabled, then the first language they
   * did enable, then the brand's default locale. Always returns a real locale, so the sentinel can
   * never survive past the single resolution point in setupWidgetLang().
   *
   * Every tier is constrained to `enabledLanguages`, so a language the partner switched off in the
   * widget cannot be rendered — a partner whose account language is it_CH but who only enabled
   * de/fr gets German, not Italian.
   *
   * `partnerDefaultLocale` is `partner.language_identifier` from the configuration response. It is
   * normalised through resolveLocale() and tolerated as absent: the widget deploys independently of
   * the API, so it must stay correct against a backend that does not send the field yet.
   */
  resolveAutoLocale(enabledLanguages: string[] | undefined, partnerDefaultLocale: string | undefined): string {
    return this.getBrowserLocale(enabledLanguages)
      ?? this.resolveEnabledLocale(partnerDefaultLocale, enabledLanguages)
      ?? this.resolveLocale(enabledLanguages?.[0])
      ?? environment.defaultLocale;
  }

  /**
   * resolveLocale() constrained to the partner's enabled languages, comparing FAMILIES the way
   * getBrowserLocale() does (the partner list holds "de", the resolved value is "de_CH").
   *
   * An absent OR empty `enabledLanguages` stays unconstrained on purpose: empty means the config
   * never arrived, and the documented fail-safe there is to degrade to the partner default rather
   * than drop to the brand locale.
   */
  private resolveEnabledLocale(
    language: string | undefined,
    enabledLanguages: string[] | undefined
  ): string | undefined {
    const resolved = this.resolveLocale(language);
    if (!resolved || !enabledLanguages?.length) {
      return resolved;
    }
    const family = this.languageOf(resolved);

    return family && enabledLanguages.includes(family) ? resolved : undefined;
  }

  loadIframeResizerJs(version: string) {
    const script = document.createElement('script');
    script.src = version === '5.5.0'
      ? 'https://cdn.jsdelivr.net/npm/@iframe-resizer/child@5.5'
      : 'https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/4.3.5/iframeResizer.contentWindow.min.js';
    script.defer = true;
    document.head.appendChild(script);
  }
}
